import ShareDialog from "@/components/cre/ShareDialog";
import IncidentBriefShareDialog from "@/components/cre/IncidentBriefShareDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Archive, Download, Loader2, Play, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";

export function ArchiveConfirm({ onConfirm, trigger }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent
        data-testid="archive-confirm-dialog"
        className="cre-surface border-white/10"
      >
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">
            Archive this incident?
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">
            It will be removed from active/recent views but retained in
            archived history with its audit trail.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            data-testid="archive-confirm-cancel"
            className="rounded-sm border-white/15"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            data-testid="archive-confirm-ok"
            onClick={onConfirm}
            className="rounded-sm bg-rose-500 hover:bg-rose-400 text-white"
          >
            Archive incident
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function WarRoomToolbar({
  incident,
  hasAnalysis,
  analyzing,
  onRunAnalysis,
  onArchive,
  onIncidentUpdate,
}) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {hasAnalysis ? (
        <Button
          onClick={onRunAnalysis}
          disabled={analyzing || incident.status === "approved"}
          variant="outline"
          size="sm"
          data-testid="rerun-analysis"
          className="rounded-sm border-white/15 text-zinc-200 hover:bg-white/5"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
          Re-run analysis
        </Button>
      ) : (
        <Button
          onClick={onRunAnalysis}
          disabled={analyzing}
          size="sm"
          data-testid="run-analysis"
          className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white font-semibold"
        >
          {analyzing ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
              Agents reasoning…
            </>
          ) : (
            <>
              <Play className="h-3.5 w-3.5 mr-2" strokeWidth={2} />
              Dispatch agents
            </>
          )}
        </Button>
      )}
      {hasAnalysis ? (
        <a
          href={api.fullReportPdfUrl(incident.id)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-full-report"
        >
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
          >
            <Download className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
            Download full analysis report
          </Button>
        </a>
      ) : null}
      {!incident.archived ? (
        <ArchiveConfirm
          onConfirm={onArchive}
          trigger={
            <Button
              variant="outline"
              size="sm"
              data-testid="archive-incident"
              className="rounded-sm border-white/15 text-zinc-300 hover:bg-white/5"
            >
              <Archive className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
              Archive
            </Button>
          }
        />
      ) : null}
      <ShareDialog incident={incident} onUpdate={onIncidentUpdate} />
      <IncidentBriefShareDialog incident={incident} />
    </div>
  );
}
